// AXIS ZERO - Leaderboard API Service
// Connects to our own FastAPI backend for global leaderboard

const STORAGE_KEYS = {
  DEVICE_ID: 'neonStageRun_deviceId',
  NICKNAME: 'neonStageRun_nickname',
  PERSONAL_BEST_SCORE: 'neonStageRun_personalBestScore',
  PERSONAL_BEST_RANK: 'neonStageRun_personalBestRank',
};

class LeaderboardAPIService {
  constructor() {
    this.deviceId = this.getOrCreateDeviceId();
    this.nickname = this.getNickname();
    this.personalBestScore = this.getStoredPersonalBestScore();
    this.personalBestRank = this.getStoredPersonalBestRank();
    this.isSubmitting = false;
  }

  // Generate UUID-like device ID
  generateDeviceId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    const randomPart2 = Math.random().toString(36).substring(2, 15);
    return `device_${timestamp}_${randomPart}${randomPart2}`;
  }

  // Get or create device ID from localStorage
  getOrCreateDeviceId() {
    try {
      let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
      if (!deviceId) {
        deviceId = this.generateDeviceId();
        localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
      }
      return deviceId;
    } catch (e) {
      // Fallback if localStorage is unavailable
      return this.generateDeviceId();
    }
  }

  // Get nickname from localStorage
  getNickname() {
    try {
      return localStorage.getItem(STORAGE_KEYS.NICKNAME) || '';
    } catch (e) {
      return '';
    }
  }

  // Set nickname and persist to localStorage
  setNickname(name) {
    const sanitized = (name || '').trim().substring(0, 12);
    this.nickname = sanitized;
    try {
      localStorage.setItem(STORAGE_KEYS.NICKNAME, sanitized);
    } catch (e) {
      // Ignore localStorage errors
    }
    return sanitized;
  }

  // Get stored personal best score from localStorage
  getStoredPersonalBestScore() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PERSONAL_BEST_SCORE);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        return isNaN(parsed) ? null : parsed;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // Get stored personal best rank from localStorage
  getStoredPersonalBestRank() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PERSONAL_BEST_RANK);
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        return isNaN(parsed) ? null : parsed;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  // Store personal best score to localStorage
  setStoredPersonalBestScore(score) {
    this.personalBestScore = score;
    try {
      localStorage.setItem(STORAGE_KEYS.PERSONAL_BEST_SCORE, String(score));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  // Store personal best rank to localStorage
  setStoredPersonalBestRank(rank) {
    this.personalBestRank = rank;
    try {
      localStorage.setItem(STORAGE_KEYS.PERSONAL_BEST_RANK, String(rank));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  // Check if a score is a new personal best (before submitting)
  isNewPersonalBest(score) {
    const currentBest = this.personalBestScore;
    if (currentBest === null) return true;
    return score > currentBest;
  }

  // Get current personal best (for display)
  getPersonalBest() {
    return {
      score: this.personalBestScore,
      rank: this.personalBestRank,
      nickname: this.nickname,
    };
  }

  // Submit score to backend
  async submitScore(score, nickname = null) {
    if (this.isSubmitting) {
      console.warn('[LeaderboardAPI] Already submitting, ignoring duplicate call');
      return null;
    }

    this.isSubmitting = true;

    try {
      // Use provided nickname or stored nickname
      const useNickname = nickname || this.nickname || 'PLAYER';
      
      // Store nickname if provided
      if (nickname) {
        this.setNickname(nickname);
      }

      const response = await fetch('/api/leaderboard/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_id: this.deviceId,
          nickname: useNickname,
          score: Math.floor(score), // Ensure integer
        }),
      });

      if (!response.ok) {
        console.error('[LeaderboardAPI] Submit failed:', response.status);
        return null;
      }

      const data = await response.json();
      
      // Update local storage with server response
      if (data.personal_best_score !== undefined) {
        this.setStoredPersonalBestScore(data.personal_best_score);
      }
      if (data.personal_best_rank !== undefined) {
        this.setStoredPersonalBestRank(data.personal_best_rank);
      }
      if (data.your_entry?.nickname) {
        this.setNickname(data.your_entry.nickname);
      }

      return {
        accepted: data.accepted,
        isPersonalBest: data.is_personal_best,
        personalBestScore: data.personal_best_score,
        personalBestRank: data.personal_best_rank,
        globalTop: data.global_top || [],
        yourEntry: data.your_entry || null,
      };
    } catch (e) {
      console.error('[LeaderboardAPI] Submit error:', e);
      return null;
    } finally {
      this.isSubmitting = false;
    }
  }

  // Fetch top scores from backend
  async fetchTopScores(limit = 10) {
    try {
      const response = await fetch(`/api/leaderboard/top?limit=${limit}`);
      
      if (!response.ok) {
        console.error('[LeaderboardAPI] Fetch top scores failed:', response.status);
        return [];
      }

      const data = await response.json();
      return data || [];
    } catch (e) {
      console.error('[LeaderboardAPI] Fetch top scores error:', e);
      return [];
    }
  }
}

// Export singleton instance
export const leaderboardAPI = new LeaderboardAPIService();
export default leaderboardAPI;
